// src/pages/Cart.js
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTheme } from '@mui/material/styles';
import { ShoppingBasket } from 'lucide-react';
import { getStorefrontProducts } from '../db';
import { ProductThumb } from '../components/ProductPhoto';
import { useSeo } from '../seo';
import { cartOrder, readStoredCart, reconcileCart, writeStoredCart } from '../products';

const rand = (value) => `R${Number(value || 0).toFixed(2)}`;

/**
 * The page frame. Declared at module scope on purpose: nested inside the
 * component it would be a new component type on every render, and React would
 * throw the Header away and rebuild it — losing its auth state each keystroke.
 */
const Shell = ({ children }) => (
  <>
    <CssBaseline />
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />
      {children}
      <Footer />
    </Box>
  </>
);

const Cart = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dark = theme.palette.mode === 'dark';
  // Olive already lightens with the mode, so the accent is simply the brand.
  const accent = theme.palette.primary.main;

  useSeo({ title: 'Your cart | Crestancia', path: '/cart', noindex: true });

  const [cart, setCart] = useState({ items: {}, prices: {}, names: {} });
  const [products, setProducts] = useState([]);
  // Unknown (absent) until read; null for every product when the read failed.
  const [stock, setStock] = useState({});
  const [loading, setLoading] = useState(true);

  /**
   * Live prices and stock, with each quantity trimmed to what can be sold —
   * anything off sale or out of stock drops out of the cart. The same reconcile
   * the dropdown does on open, so arriving here directly cannot show a bale
   * that can no longer be bought.
   */
  useEffect(() => {
    let active = true;
    async function load() {
      const stored = readStoredCart();
      let next = stored;

      try {
        const data = await getStorefrontProducts();
        if (!active) return;
        const live = reconcileCart(stored, data);
        next = { items: live.items, prices: live.prices, names: live.names };
        setProducts(data);
        setStock(live.stock);
      } catch (err) {
        if (!active) return;
        // An unreadable catalogue must not empty someone's basket, so the
        // stored quantities stand, at the prices they were last shown at, and
        // no stock limit is known.
        const unknown = {};
        Object.keys(stored.items).forEach((id) => {
          unknown[id] = null;
        });
        setStock(unknown);
        toast.error('Could not refresh prices: ' + err.message);
      }

      setCart(next);
      setLoading(false);
      // Store what was actually shown, so checkout reads the same numbers.
      writeStoredCart(next.items, next.prices, next.names);
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  // The header dropdown edits the same cart. Without this the two disagree
  // until the page is reloaded.
  useEffect(() => {
    const sync = () => {
      const stored = readStoredCart();
      setCart((current) => {
        const ids = new Set([...Object.keys(stored.items), ...Object.keys(current.items)]);
        const changed = [...ids].some((id) => (stored.items[id] || 0) !== (current.items[id] || 0));
        return changed
          ? {
              items: stored.items,
              prices: { ...current.prices, ...stored.prices },
              names: { ...current.names, ...stored.names },
            }
          : current;
      });
    };
    window.addEventListener('cartUpdated', sync);
    return () => window.removeEventListener('cartUpdated', sync);
  }, []);

  const commit = useCallback(
    (items) => {
      setCart((current) => ({ ...current, items }));
      writeStoredCart(items, cart.prices, cart.names);
    },
    [cart.prices, cart.names],
  );

  const setQuantity = (product, value) => {
    const limit = stock[product.id];
    if (limit !== null && limit !== undefined && value > limit) {
      toast.warning(`Only ${limit} ${product.name} left in stock.`);
      return;
    }
    commit({ ...cart.items, [product.id]: Math.max(0, value) });
  };

  const removeItem = (product) => {
    commit({ ...cart.items, [product.id]: 0 });
    toast.info(`${product.name} removed from your cart`);
  };

  const clearCart = () => {
    commit({});
    toast.info('Cart has been cleared');
  };

  const lines = cartOrder(cart.items, products)
    .map((id) => {
      const quantity = cart.items[id] || 0;
      const price = cart.prices[id] || 0;
      const product = { id, name: cart.names[id] || '' };
      return { product, quantity, price, subtotal: quantity * price };
    })
    .filter((line) => line.quantity > 0);

  const totalItems = lines.reduce((sum, line) => sum + line.quantity, 0);
  const subtotal = lines.reduce((sum, line) => sum + line.subtotal, 0);

  const handleCheckout = () => {
    if (totalItems <= 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  if (loading) {
    return (
      <Shell>
        <Container maxWidth="lg" sx={{ flexGrow: 1, py: { xs: 4, md: 6 } }}>
          <Skeleton variant="text" width={220} height={54} />
          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.9fr) minmax(0, 1fr)' },
            }}
          >
            <Stack spacing={2}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={116} />
              ))}
            </Stack>
            <Skeleton variant="rounded" height={280} />
          </Box>
        </Container>
      </Shell>
    );
  }

  if (totalItems <= 0) {
    return (
      <Shell>
        <Container
          maxWidth="sm"
          sx={{
            flexGrow: 1,
            py: { xs: 6, md: 10 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 76,
                height: 76,
                mx: 'auto',
                mb: 3,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: accent,
                backgroundColor: dark ? 'rgba(179,199,127,0.12)' : 'rgba(85,107,47,0.09)',
              }}
            >
              <ShoppingBasket size={34} strokeWidth={1.6} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
              Your cart is empty
            </Typography>
            <Typography sx={{ mb: 4, color: 'text.secondary' }}>
              Nothing in the basket yet. Have a look at what is baled and ready.
            </Typography>
            <Button
              component={RouterLink}
              to="/home#products"
              variant="contained"
              size="large"
              disableElevation
              endIcon={<ArrowForwardIcon />}
              sx={{ borderRadius: 999, px: 4, py: 1.4, fontWeight: 700, textTransform: 'none' }}
            >
              Browse products
            </Button>
          </Paper>
        </Container>
      </Shell>
    );
  }

  return (
    <Shell>
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: { xs: 4, md: 6 } }}>
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 3 }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Your cart
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {totalItems} {totalItems === 1 ? 'item' : 'items'}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gap: 3,
            alignItems: 'start',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.9fr) minmax(0, 1fr)' },
          }}
        >
          <Stack spacing={2}>
            {lines.map(({ product, quantity, price, subtotal: lineTotal }) => {
              const limit = stock[product.id];
              const atLimit = limit !== null && limit !== undefined && quantity >= limit;
              return (
                <Paper
                  key={product.id}
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'border-color .2s ease, box-shadow .2s ease',
                    '&:hover': {
                      borderColor: dark ? 'rgba(179,199,127,0.35)' : 'rgba(85,107,47,0.3)',
                      boxShadow: dark
                        ? '0 6px 20px rgba(0,0,0,0.35)'
                        : '0 6px 20px rgba(60,54,40,0.07)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gap: { xs: 2, sm: 2.5 },
                      alignItems: 'center',
                      gridTemplateColumns: { xs: '72px 1fr', sm: '92px 1fr auto' },
                    }}
                  >
                    <ProductThumb
                      productId={product.id}
                      alt={product.name}
                      size={92}
                      sx={{
                        width: { xs: 72, sm: 92 },
                        height: { xs: 72, sm: 92 },
                        borderRadius: 2.5,
                        gridRow: { xs: 'span 2', sm: 'auto' },
                      }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3 }}>
                        {product.name}
                      </Typography>
                      <Typography sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.9rem' }}>
                        {rand(price)} each
                      </Typography>
                      {atLimit && (
                        <Chip
                          size="small"
                          label={`All ${limit} in stock`}
                          sx={{ mt: 1, height: 22, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>

                    <Stack
                      direction={{ xs: 'row', sm: 'column' }}
                      alignItems={{ xs: 'center', sm: 'flex-end' }}
                      justifyContent="space-between"
                      spacing={{ xs: 2, sm: 1.25 }}
                      sx={{ gridColumn: { xs: '2', sm: 'auto' } }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                          borderRadius: 999,
                          border: '1px solid',
                          borderColor: 'divider',
                          px: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label={`Remove one ${product.name}`}
                          onClick={() => setQuantity(product, quantity - 1)}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: 26, textAlign: 'center', fontWeight: 700 }}>
                          {quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Add one ${product.name}`}
                          disabled={atLimit}
                          onClick={() => setQuantity(product, quantity + 1)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                          {rand(lineTotal)}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${product.name} from cart`}
                          onClick={() => removeItem(product)}
                          sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                </Paper>
              );
            })}

            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ pt: 1 }}>
              <Button
                component={RouterLink}
                to="/home#products"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                &larr; Continue shopping
              </Button>
              <Button
                onClick={clearCart}
                startIcon={<DeleteOutlineIcon />}
                sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
              >
                Clear cart
              </Button>
            </Stack>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              position: { md: 'sticky' },
              top: { md: 96 },
            }}
          >
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Order summary
            </Typography>

            <Stack spacing={1.25} sx={{ mt: 2.5 }}>
              {lines.map(({ product, quantity, subtotal: lineTotal }) => (
                <Stack key={product.id} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                    {product.name} &times; {quantity}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                    {rand(lineTotal)}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography sx={{ fontWeight: 700 }}>Subtotal</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.45rem' }}>{rand(subtotal)}</Typography>
            </Stack>
            <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
              Delivery codes and discounts are applied at checkout.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              size="large"
              disableElevation
              onClick={handleCheckout}
              endIcon={<ArrowForwardIcon />}
              sx={{
                mt: 3,
                py: 1.5,
                borderRadius: 999,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              Proceed to checkout
            </Button>
          </Paper>
        </Box>
      </Container>
    </Shell>
  );
};

export default Cart;
